import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import IconSelectorModal from '../../components/feature/IconSelectorModal';
import FamilyService, { type FamilyMember } from '../../services/familyService';
import { OnboardingLimitModal } from '../../components/payment';

interface Member {
  name: string;
  birthDate: string;
  gender: 'male' | 'female';
  avatar: string;
}

export default function AddMemberPage() {
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [currentMember, setCurrentMember] = useState<Member>({
    name: '',
    birthDate: '',
    gender: 'male',
    avatar: 'ri-user-line'
  });
  const [showIconModal, setShowIconModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [_loading, setLoading] = useState(false);
  const [_error, setError] = useState('');
  const familyService = FamilyService.getInstance();

  const handleInputChange = (field: keyof Member) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setCurrentMember(prev => ({
      ...prev,
      [field]: value
    }));
    try {
      if (field === 'name' && value.trim().length > 0) {
        window.dispatchEvent(new CustomEvent('tutorial:member:name-filled'))
      }
      if (field === 'birthDate' && value) {
        window.dispatchEvent(new CustomEvent('tutorial:member:birthdate-filled'))
      }
    } catch { /* ignore */ }
  };

  const handleGenderSelect = (gender: 'male' | 'female') => {
    setCurrentMember(prev => ({
      ...prev,
      gender,
      avatar: gender === 'male' ? 'ri-user-line' : 'ri-user-2-line'
    }));
    // Abrir modal de seleção de ícones
    setShowIconModal(true);
    // Sinalizar seleção de gênero para o tutorial
    try { window.dispatchEvent(new CustomEvent('tutorial:member:gender-selected')) } catch { /* ignore */ }
  };

  const handleIconSelect = (icon: string) => {
    setCurrentMember(prev => ({
      ...prev,
      avatar: icon
    }));
  };

  const addMember = () => {
    // Validar limite de 3 membros (plano free)
    if (members.length >= 3) {
      setShowLimitModal(true);
      return;
    }

    if (currentMember.name && currentMember.birthDate) {
      setMembers(prev => [...prev, currentMember]);
      setCurrentMember({
        name: '',
        birthDate: '',
        gender: 'male',
        avatar: 'ri-user-line'
      });
    }
  };

  const removeMember = (index: number) => {
    setMembers(prev => prev.filter((_, i) => i !== index));
  };

  const handleContinue = async () => {
    if (members.length === 0) {
      setError('Adicione pelo menos um membro da família');
      return;
    }

    // Bloqueio adicional no onboarding: até 3 membros no plano gratuito
    // Evita tentativa de persistência caso a lista local exceda o limite
    if (members.length > 3) {
      setShowLimitModal(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    console.log('🚀 [AddMemberPage] Salvando membros no banco de dados');

    try {
      // Buscar ID da família do localStorage
      const familyId = localStorage.getItem('currentFamilyId');
      
      if (!familyId) {
        throw new Error('ID da família não encontrado. Por favor, configure a família primeiro.');
      }

      // Salvar cada membro no banco de dados
      for (const member of members) {
        const memberData: FamilyMember = {
          name: member.name,
          birth_date: member.birthDate,
          date_of_birth: member.birthDate,
          gender: member.gender,
          avatar_url: member.avatar,
          is_main_user: false
        };

        await familyService.addFamilyMember(familyId, memberData);
        console.log('✅ [AddMemberPage] Membro salvo:', member.name);
      }

      console.log('✅ [AddMemberPage] Todos os membros foram salvos');
      
      // Limpar localStorage
      localStorage.removeItem('currentFamilyId');
      
      // Navegar para dashboard
      navigate('/dashboard');
    } catch (err: any) {
      console.error('❌ [AddMemberPage] Erro ao salvar membros:', err);
      setError(err.message || 'Erro ao salvar membros. Tente novamente.');
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate('/setup-family');
  };

  // Função para verificar se é uma imagem
  const isImageIcon = (icon: string) => icon.startsWith('/Imagens/') || icon.startsWith('http') || icon.startsWith('data:');

  // Sinalizar ao Guided Tutorial que a tela de adicionar membro está pronta (Step 4)
  useEffect(() => {
    try {
      const sel = '[data-tutorial-anchor="member-name-input"]'
      const el = document.querySelector(sel)
      if (el) {
        window.dispatchEvent(new CustomEvent('tutorial:add-member:ready'))
        return
      }
      const id = window.setTimeout(() => {
        const el2 = document.querySelector(sel)
        if (el2) {
          window.dispatchEvent(new CustomEvent('tutorial:add-member:ready'))
        }
      }, 60)
      return () => window.clearTimeout(id)
    } catch { /* ignore */ }
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-t from-purple-100 to-white px-6 py-8 pb-20">
      {/* Header com botão voltar */}
      <div className="flex items-center justify-between mb-6 pt-8">
        <button
          onClick={goBack}
          className="w-10 h-10 bg-white rounded-2xl flex items-center justify-center shadow-md"
        >
          <i className="ri-arrow-left-line text-purple-600 text-xl"></i>
        </button>
        <div className="text-center flex-1">
          <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center mb-4 mx-auto shadow-lg">
            <i className="ri-group-line text-3xl text-purple-500"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2 font-sans" style={{ 
            fontWeight: 700,
            textShadow: '1px 2px 4px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.05)'
          }}>Adicionar Membros</h1>
          <p className="text-gray-600 text-sm font-sans" style={{ fontWeight: 500 }}>Cadastre cada pessoa da família</p>
        </div>
        <div className="w-10"></div>
      </div>

      {/* Formulário de Novo Membro */}
      <div className="bg-white rounded-3xl p-6 shadow-xl mb-6">
        <div className="space-y-4">
          <div data-tutorial-anchor="member-name-input">
            <Input
              label="Nome"
              type="text"
              placeholder="Nome do membro"
              value={currentMember.name}
              onChange={handleInputChange('name')}
              required
            />
          </div>
          
          <div data-tutorial-anchor="member-birthdate-input">
            <Input
              label="Data de Nascimento"
              type="date"
              value={currentMember.birthDate}
              onChange={handleInputChange('birthDate')}
              required
            />
          </div>

          {/* Seleção de Gênero */}
          <div data-tutorial-anchor="member-gender-select">
            <label className="block text-sm font-medium text-gray-700 mb-3 font-sans" style={{ fontWeight: 500 }}>
              Sexo <span className="text-red-400">*</span>
            </label>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-3">
              <button
                type="button"
                onClick={() => handleGenderSelect('male')}
                className={`p-6 rounded-2xl border-2 transition-all ${
                  currentMember.gender === 'male'
                    ? 'border-purple-300 bg-purple-50 text-purple-600'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                <div className="w-24 h-24 mx-auto mb-3 flex items-center justify-center overflow-hidden rounded-xl bg-white/50 shadow-sm">
                  {currentMember.gender === 'male' && isImageIcon(currentMember.avatar) ? (
                    <img 
                      src={currentMember.avatar} 
                      alt="Avatar masculino" 
                      className="w-full h-full object-cover rounded-lg"
                      style={{ imageRendering: 'crisp-edges' }}
                    />
                  ) : (
                    <i className={`${currentMember.gender === 'male' ? currentMember.avatar : 'ri-user-line'} text-4xl`}></i>
                  )}
                </div>
                <span className="text-sm font-medium font-sans" style={{ fontWeight: 500 }}>Masculino</span>
              </button>
              <button
                type="button"
                onClick={() => handleGenderSelect('female')}
                className={`p-6 rounded-2xl border-2 transition-all ${
                  currentMember.gender === 'female'
                    ? 'border-purple-300 bg-purple-50 text-purple-600'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                <div className="w-24 h-24 mx-auto mb-3 flex items-center justify-center overflow-hidden rounded-xl bg-white/50 shadow-sm">
                  {currentMember.gender === 'female' && isImageIcon(currentMember.avatar) ? (
                    <img 
                      src={currentMember.avatar} 
                      alt="Avatar feminino" 
                      className="w-full h-full object-cover rounded-lg"
                      style={{ imageRendering: 'crisp-edges' }}
                    />
                  ) : (
                    <i className={`${currentMember.gender === 'female' ? currentMember.avatar : 'ri-user-2-line'} text-4xl`}></i>
                  )}
                </div>
                <span className="text-sm font-medium font-sans" style={{ fontWeight: 500 }}>Feminino</span>
              </button>
            </div>
          </div>

          <div data-tutorial-anchor="btn-add-member">
            <Button
              onClick={addMember}
              variant="secondary"
              size="lg"
              className="w-full"
              disabled={!currentMember.name || !currentMember.birthDate}
            >
              <i className="ri-add-line mr-2"></i>
              Adicionar Membro
            </Button>
          </div>
        </div>
      </div>

      {/* Lista de Membros Adicionados */}
      {members.length > 0 && (
        <div className="bg-white rounded-3xl p-6 shadow-xl mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 font-sans" style={{ fontWeight: 700 }}>
            Membros da Família ({members.length})
          </h3>
          <div className="space-y-3">
            {members.map((member, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-purple-50 rounded-2xl border border-purple-100">
                <div className="flex items-center">
                  <div className="w-28 h-28 bg-white rounded-xl flex items-center justify-center mr-4 overflow-hidden shadow-sm border border-purple-100">
                    {isImageIcon(member.avatar) ? (
                      <img 
                        src={member.avatar} 
                        alt={`Avatar de ${member.name}`} 
                        className="w-full h-full object-cover rounded-lg"
                        style={{ imageRendering: 'crisp-edges' }}
                      />
                    ) : (
                      <i className={`${member.avatar} text-purple-500 text-3xl`}></i>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 font-sans" style={{ fontWeight: 700 }}>{member.name}</p>
                    <p className="text-sm text-gray-600 font-sans" style={{ fontWeight: 500 }}>
                      {new Date(member.birthDate).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeMember(index)}
                  className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-200 transition-colors"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progresso */}
      <div className="flex justify-center mt-6">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
          <div className="w-8 h-2 bg-purple-400 rounded-full"></div>
          <div className="w-2 h-2 bg-gray-200 rounded-full"></div>
        </div>
      </div>

      {/* Botão Continuar */}
      {members.length > 0 && (
        <div className="fixed bottom-6 left-6 right-6" data-tutorial-anchor="btn-continue-add-members">
          <Button
            onClick={handleContinue}
            size="lg"
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            <i className="ri-arrow-right-line mr-2"></i>
            Continuar ({members.length} {members.length === 1 ? 'membro' : 'membros'})
          </Button>
        </div>
      )}
      {/* Modal de Seleção de Ícones */}
      <IconSelectorModal
        isOpen={showIconModal}
        onClose={() => setShowIconModal(false)}
        onSelectIcon={handleIconSelect}
        selectedIcon={currentMember.avatar}
        gender={currentMember.gender}
        memberName={currentMember.name}
      />

      {/* Modal de Limite do Plano Gratuito */}
      {showLimitModal && (
        <OnboardingLimitModal onClose={() => setShowLimitModal(false)} />
      )}
    </div>
  );
}






